import type { ThemeId } from '@recipes/contracts';
import { cn } from '@recipes/ui';

/**
 * The picture of a theme (docs/partial/appearance-and-themes-spec.md 6.3): an illustration
 * drawn with the theme's own tokens, so it follows theme and mode by itself, weighs a few
 * kilobytes, is part of the bundle (no request, nothing to cache, works offline) and never
 * shows a dish that does not exist. Own work; no photographs are bundled.
 *
 * Decorative – always `aria-hidden`. Text never sits on it (see `.app-surface`).
 */
export function ThemePicture({ theme, className }: { theme: ThemeId; className?: string }) {
  const Picture = theme === 'confetti' ? ConfettiPicture : ClassicPicture;
  return <Picture className={cn('block', className)} />;
}

const FRAME = {
  viewBox: '0 0 1200 600',
  preserveAspectRatio: 'xMidYMax slice',
  'aria-hidden': true,
  focusable: false,
} as const;

/** Classic: a quiet kitchen shelf – pot, board, bread, oil and herbs in warm colours. */
function ClassicPicture({ className }: { className?: string }) {
  return (
    <svg {...FRAME} className={className} data-testid="theme-picture" data-theme-picture="classic">
      <rect width="1200" height="600" className="fill-cat-4-surface" />
      <circle cx="960" cy="150" r="110" className="fill-cat-7-surface" />
      {/* tiles */}
      <g className="stroke-cat-4 opacity-30" strokeWidth="2" fill="none">
        <path d="M0 120H1200M0 240H1200M0 360H1200" />
        <path d="M150 0V420M350 0V420M550 0V420M750 0V420M950 0V420M1150 0V420" />
      </g>
      {/* counter */}
      <rect y="420" width="1200" height="180" className="fill-cat-4" />
      <rect y="420" width="1200" height="14" className="fill-cat-7" />
      {/* pot with steam */}
      <g>
        <path
          d="M250 250c-20-30 20-40 0-70M310 240c-20-30 20-40 0-70M370 250c-20-30 20-40 0-70"
          className="stroke-cat-4"
          strokeWidth="8"
          strokeLinecap="round"
          fill="none"
        />
        <rect x="190" y="300" width="250" height="125" rx="18" className="fill-cat-2" />
        <rect x="175" y="285" width="280" height="26" rx="13" className="fill-cat-2-foreground" />
        <rect x="295" y="262" width="40" height="26" rx="10" className="fill-cat-2-foreground" />
        <rect x="150" y="330" width="46" height="18" rx="9" className="fill-cat-2-foreground" />
        <rect x="434" y="330" width="46" height="18" rx="9" className="fill-cat-2-foreground" />
      </g>
      {/* board with bread */}
      <g>
        <rect x="540" y="398" width="300" height="26" rx="10" className="fill-cat-7" />
        <path d="M580 398c0-60 40-88 100-88s100 28 100 88z" className="fill-cat-6" />
        <path
          d="M625 345l20 22M675 335l20 24M725 345l20 22"
          className="stroke-cat-4-foreground"
          strokeWidth="6"
          strokeLinecap="round"
        />
      </g>
      {/* oil bottle */}
      <g>
        <rect x="905" y="250" width="70" height="175" rx="16" className="fill-cat-1" />
        <rect x="926" y="205" width="28" height="55" rx="6" className="fill-cat-1" />
        <rect x="922" y="192" width="36" height="20" rx="6" className="fill-cat-4-foreground" />
        <rect x="917" y="310" width="46" height="60" rx="6" className="fill-cat-1-surface" />
      </g>
      {/* herbs in a pot */}
      <g>
        <path d="M1030 360h110l-16 65h-78z" className="fill-cat-7" />
        <g className="fill-cat-1">
          <ellipse cx="1060" cy="330" rx="22" ry="38" transform="rotate(-25 1060 330)" />
          <ellipse cx="1112" cy="328" rx="22" ry="40" transform="rotate(22 1112 328)" />
          <ellipse cx="1086" cy="308" rx="20" ry="46" />
        </g>
      </g>
      {/* tomatoes */}
      <g>
        <circle cx="80" cy="396" r="30" className="fill-cat-2" />
        <circle cx="128" cy="402" r="24" className="fill-cat-2" />
        <path
          d="M80 366l-8-12M80 366l9-11M128 378l-6-10M128 378l8-9"
          className="stroke-cat-1"
          strokeWidth="5"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}

/** Confetti: bunting, fruit and paper dots – a kitchen party. */
function ConfettiPicture({ className }: { className?: string }) {
  const flags = [1, 10, 6, 5, 8, 7, 3, 9, 2, 1, 10, 6] as const;
  const dots: Array<[number, number, number, number]> = [
    [90, 250, 10, 5],
    [210, 330, 7, 6],
    [330, 230, 12, 10],
    [470, 300, 8, 8],
    [600, 240, 11, 1],
    [730, 320, 7, 7],
    [860, 250, 10, 3],
    [990, 310, 8, 9],
    [1110, 240, 12, 6],
    [150, 410, 6, 2],
    [1050, 420, 7, 5],
    [560, 400, 6, 10],
  ];
  return (
    <svg {...FRAME} className={className} data-testid="theme-picture" data-theme-picture="confetti">
      <rect width="1200" height="600" className="fill-cat-6-surface" />
      <circle cx="1020" cy="120" r="150" className="fill-cat-10-surface" />
      <circle cx="160" cy="90" r="120" className="fill-cat-5-surface" />
      {/* bunting */}
      <path d="M-10 40Q600 210 1210 40" className="stroke-cat-8" strokeWidth="5" fill="none" />
      {flags.map((hue, index) => {
        const x = 40 + index * 100;
        // Where the string (the quadratic curve above) is at the middle of the flag.
        const t = (x + 40) / 1220;
        const y = Math.round(40 + 340 * t * (1 - t));
        return <path key={index} d={`M${x} ${y - 4}h60l-30 70z`} className={FILL[hue]} />;
      })}
      {dots.map(([cx, cy, r, hue], index) => (
        <circle key={index} cx={cx} cy={cy} r={r} className={FILL[hue as keyof typeof FILL]} />
      ))}
      {/* table */}
      <rect y="470" width="1200" height="130" className="fill-cat-10" />
      <g className="fill-cat-10-surface opacity-60">
        <circle cx="100" cy="535" r="14" />
        <circle cx="300" cy="560" r="14" />
        <circle cx="500" cy="530" r="14" />
        <circle cx="700" cy="565" r="14" />
        <circle cx="900" cy="535" r="14" />
        <circle cx="1100" cy="560" r="14" />
      </g>
      {/* orange, halved */}
      <g>
        <circle cx="300" cy="420" r="78" className="fill-cat-7" />
        <circle cx="300" cy="420" r="64" className="fill-cat-7-surface" />
        <g className="stroke-cat-7" strokeWidth="6" strokeLinecap="round">
          <path d="M300 364v112M244 420h112M260 380l80 80M340 380l-80 80" />
        </g>
      </g>
      {/* lemon */}
      <g>
        <ellipse cx="480" cy="445" rx="70" ry="50" className="fill-cat-6" />
        <circle cx="548" cy="445" r="9" className="fill-cat-6" />
        <path
          d="M440 430q20-18 50-14"
          className="stroke-cat-6-surface"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
        />
      </g>
      {/* strawberry */}
      <g>
        <path
          d="M690 380c60-10 96 30 60 84-16 24-40 40-60 46-20-6-44-22-60-46-36-54 0-94 60-84z"
          className="fill-cat-2"
        />
        <path d="M690 384l-34-26 22 4-4-24 16 20 16-20-4 24 22-4z" className="fill-cat-1" />
        <g className="fill-cat-2-surface">
          <circle cx="664" cy="424" r="4" />
          <circle cx="694" cy="440" r="4" />
          <circle cx="722" cy="420" r="4" />
          <circle cx="678" cy="466" r="4" />
          <circle cx="708" cy="474" r="4" />
        </g>
      </g>
      {/* avocado */}
      <g>
        <path
          d="M900 330c46 0 78 56 78 104s-34 68-78 68-78-20-78-68 32-104 78-104z"
          className="fill-cat-1"
        />
        <path
          d="M900 352c34 0 58 44 58 82s-26 50-58 50-58-12-58-50 24-82 58-82z"
          className="fill-cat-1-surface"
        />
        <circle cx="900" cy="440" r="28" className="fill-cat-4" />
      </g>
      {/* blueberries */}
      <g className="fill-cat-3">
        <circle cx="1060" cy="470" r="20" />
        <circle cx="1096" cy="452" r="20" />
        <circle cx="1124" cy="480" r="20" />
      </g>
    </svg>
  );
}

/** Tailwind sees class names only when they are written out. */
const FILL = {
  1: 'fill-cat-1',
  2: 'fill-cat-2',
  3: 'fill-cat-3',
  4: 'fill-cat-4',
  5: 'fill-cat-5',
  6: 'fill-cat-6',
  7: 'fill-cat-7',
  8: 'fill-cat-8',
  9: 'fill-cat-9',
  10: 'fill-cat-10',
} as const;
